/**
 * `<SearchableSelect>` — pick ONE option from a dropdown that has a search
 * box (newsletter-tag-audience, 2026-10-02: the `/newsletter` tag picker —
 * Mario: "a dropdown of all tags, and you can also search for them").
 *
 * The SPA had no such control: `SearchableLookupField` is an input over an
 * always-open native listbox, and `MultiSelectField` is a dropdown without a
 * search. This one is composed from their proven pieces rather than invented —
 * `MultiSelectField`'s trigger-and-panel with the outside-click close, its
 * panel's look (`.multi-select-menu`'s pop surface and shadow), and a search
 * box pinned at the panel's top. Opening it moves focus into the search box;
 * typing filters (every word must match the label or the option's hidden
 * keywords — `filterSearchableOptions`), the arrow keys move the highlight,
 * Enter picks, Escape closes back onto the trigger.
 *
 * The caller owns the options and the value; `options === null` is "still
 * loading". Inside a disabled `<fieldset>` the trigger is disabled with it.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import {
  filterSearchableOptions,
  moveSearchableActiveIndex,
  type SearchableSelectOption,
} from "../lib/searchable-select";

export type { SearchableSelectOption } from "../lib/searchable-select";

export function SearchableSelect({
  options,
  value,
  onChange,
  ariaLabel,
  placeholder,
  searchPlaceholder,
  emptyText,
  noMatchText,
  loadError,
}: {
  /** `null` while the options load. */
  readonly options: ReadonlyArray<SearchableSelectOption> | null;
  readonly value: string | null;
  readonly onChange: (value: string) => void;
  /** Names the control for assistive tech (there is no visible label). */
  readonly ariaLabel: string;
  /** The closed dropdown's text while nothing is chosen. */
  readonly placeholder: string;
  readonly searchPlaceholder: string;
  /** Shown when there are no options at all. */
  readonly emptyText: string;
  /** Shown when the search matches nothing; receives what was typed. */
  readonly noMatchText: (query: string) => string;
  /** Why the options could not be loaded, if they could not. */
  readonly loadError?: string | null;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const listId = useId();

  const all = useMemo(() => options ?? [], [options]);
  const shown = useMemo(() => filterSearchableOptions(all, query), [all, query]);
  const chosen = all.find((o) => o.value === value) ?? null;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    // Escape closes back onto the trigger whatever holds focus, as in
    // `PopMenu` and `MultiSelectField`.
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Keep the highlighted row in view as the arrow keys move it.
  useEffect(() => {
    if (!open || active < 0) return;
    const row = listRef.current?.children.item(active);
    if (row instanceof HTMLElement) row.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  const openMenu = (): void => {
    setQuery("");
    setActive(Math.max(0, all.findIndex((o) => o.value === value)));
    setOpen(true);
  };

  const close = (refocus: boolean): void => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  const pick = (o: SearchableSelectOption): void => {
    onChange(o.value);
    close(true);
  };

  const onSearchKey = (e: KeyboardEvent<HTMLInputElement>): void => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActive((i) => moveSearchableActiveIndex(i, 1, shown.length));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActive((i) => moveSearchableActiveIndex(i, -1, shown.length));
        break;
      case "Enter": {
        e.preventDefault();
        const o = shown[active];
        if (o !== undefined) pick(o);
        break;
      }
      case "Tab":
        close(false);
        break;
      default:
        break;
    }
  };

  const optionId = (i: number): string => `${listId}-option-${String(i)}`;
  const status =
    loadError !== undefined && loadError !== null
      ? loadError
      : options === null
        ? "Loading…"
        : all.length === 0
          ? emptyText
          : shown.length === 0
            ? noMatchText(query.trim())
            : null;

  return (
    <div className="searchable-select" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="searchable-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={chosen === null ? ariaLabel : `${ariaLabel}: ${chosen.label}`}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            e.preventDefault();
            openMenu();
          }
        }}
      >
        {chosen === null ? (
          <span className="searchable-select-placeholder">{placeholder}</span>
        ) : (
          <span className="searchable-select-chosen">
            <span className="searchable-select-label">{chosen.label}</span>
            {chosen.meta !== undefined && (
              <span className="searchable-select-meta">{chosen.meta}</span>
            )}
          </span>
        )}
        <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div
          className="searchable-select-menu"
          // Keep focus in the search box whatever in the menu is pressed — a
          // row (so the click lands as a pick), the status line, the padding —
          // so the keys keep working.
          onMouseDown={(e) => {
            if (!(e.target instanceof HTMLInputElement)) e.preventDefault();
          }}
        >
          <input
            type="text"
            className="searchable-select-search"
            role="combobox"
            aria-label={ariaLabel}
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={active >= 0 && active < shown.length ? optionId(active) : undefined}
            placeholder={searchPlaceholder}
            value={query}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onSearchKey}
          />
          {status !== null && (
            <div className="searchable-select-status" role="status">
              {status}
            </div>
          )}
          <ul
            className="searchable-select-list"
            role="listbox"
            id={listId}
            aria-label={ariaLabel}
            ref={listRef}
          >
            {status === null &&
              shown.map((o, i) => (
                <li
                  key={o.value}
                  id={optionId(i)}
                  role="option"
                  aria-selected={o.value === value}
                  aria-label={o.meta === undefined ? o.label : `${o.label}, ${o.meta}`}
                  className={
                    i === active ? "searchable-select-option is-active" : "searchable-select-option"
                  }
                  // mousemove, not mouseenter: a list scrolled under a resting
                  // pointer fires mouseenter, which would take the keyboard's
                  // highlight back to whatever row lands under it.
                  onMouseMove={() => {
                    if (i !== active) setActive(i);
                  }}
                  onClick={() => pick(o)}
                >
                  <span className="searchable-select-label">{o.label}</span>
                  {o.meta !== undefined && (
                    <span className="searchable-select-meta">{o.meta}</span>
                  )}
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
